import * as PIXI from "pixi.js";

import { Howler } from "howler";

import { AutoplayPopup } from "@src/core/ui/common/popups/AutoplayPopup";
import { CheatsPopup } from "@src/core/ui/common/popups/CheatsPopup";
import { InfoPopup } from "@src/core/ui/common/popups/InfoPopup";
import { BetsPopup } from "@src/core/ui/common/popups/BetsPopup";
import { LoadingScreen } from "@src/game/screens/LoadingScreen";
import { TitledButton } from "@src/core/ui/common/TitledButton";
import { TitledInfo } from "@src/core/ui/common/TitledInfo";
import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import { Button } from "@src/core/ui/components/Button";
import { Popup } from "@src/core/ui/components/Popup";
import { Assets } from "@src/core/assets/Assets";
import {
  EGameState,
  TInitialResponse,
  TSpinResponse,
} from "@src/server/ResponseTypes";
import { assetManifest } from "@src/game/assets/AssetImports";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";

type TUIAssetAlias = keyof typeof assetManifest &
  (`${string}UI${string}` | `UI${string}`);

export type TGameUIButtonType =
  (typeof constants.UIButtonTypes)[keyof typeof constants.UIButtonTypes];

// TODO: Remake the UI atlas with proper snake case named sprites
export class GameUI implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _backgroundHorizontal!: PIXI.Sprite;
  private _backgroundVertical!: PIXI.Sprite;
  // TODO: Remove this and make the backgrounds taller.
  private _bottomCoverArea!: PIXI.Graphics;

  private _autoplayValueText!: PIXI.Text;

  private _balanceInfoDisplay!: TitledInfo;
  private _winningsInfoDisplay!: TitledInfo;

  private _betButton!: TitledButton;
  private _soundButton!: Button;
  private _cheatButton!: Button;
  // private _buyButton!: Button;
  // private _menuButton!: Button;
  // private _homeButton!: Button;
  private _infoButton!: Button;
  private _autoplayButton!: Button;
  private _swiftButton!: Button;
  private _spinButton!: Button;

  private _betsPopup!: BetsPopup;
  private _cheatsPopup!: CheatsPopup;
  private _autoplayPopup!: AutoplayPopup;
  private _infoPopup!: InfoPopup;
  // private _spinButton: SpinButton;
  // private _fastButton: FastButton;
  // private _selectBetButton: BetButton;
  // private _bottomBar: Sprite;

  private _visiblePopup!: Popup;

  private _readyToPlaySpinStopSound = false;

  // private autoplayAmount: number = 0;

  private _lastSpacebarPressTime: number = 0;
  private static readonly SPACEBAR_DEBOUNCE_MS = 500;

  public constructor() {
    this.onModifiedAutoplayCount = this.onModifiedAutoplayCount.bind(this);
    this.onSelectedAutoplayCount = this.onSelectedAutoplayCount.bind(this);

    this._setupEventListeners();

    this._initializeAssets();
    this._parentAssets();

    this._initializeComponents();
    this._parentComponents();

    this._view.zIndex = 4;
  }

  private onModifiedAutoplayCount(autoplayCount: number | "∞"): void {
    console.log("modified autoplay count");
    this._autoplayValueText.style.fill = 0xfb6f2e;
    if (typeof autoplayCount == "number") {
      this._autoplayValueText.text = autoplayCount > -1 ? autoplayCount : "";
      if (autoplayCount <= 0) {
        this._autoplayValueText.text = "";
        this._spinButton.setActive(true)
        this._autoplayValueText.style.fill = 0xffffff;
      } else {
        this._spinButton.setActive(false)
      }
    } else {
      if (autoplayCount === "∞") {
        this._autoplayValueText.text = autoplayCount;
        this._spinButton.setActive(true)
      }
    }
  }

  private onSelectedAutoplayCount(autoplayCount: number | "∞"): void {
    this._autoplayValueText.text = autoplayCount;
    this._autoplayValueText.style.fill = 0xffffff;
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(constants.UIEvents.SELECTED_AUTOPLAY_COUNT, this.onSelectedAutoplayCount);

    EventSystem.addListener(constants.GameEvents.MODIFIED_AUTOPLAY_COUNT, this.onModifiedAutoplayCount);

    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequest.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequest.bind(this),
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_INTERACTABLE_AREA,
      this._onClickedInteractableArea.bind(this),
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_UI_BUTTON,
      this._onClickedUIButton.bind(this),
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_RIG_BUTTON,
      this._onClickedRigButton.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_INITIALIZE,
      this._onGameInitialized.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_PERFORM_ACTION,
      this._onPerformedGameAction.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.WINNING_POPUP_CLOSED,
      this._onWinningPopupClosed.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.REEL_PANEL_SPIN_STOPPED,
      this._onSpinStopped.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.FREESPIN_INTRO_CLOSED,
      this._onFreespinIntroClosed.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.FREESPIN_OUTRO_CLOSED,
      this._onFreespinOutroClosed.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SHOWN,
      this._onCellPanelShown.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SPIN_STOPPED,
      this._onCellPanelSpinStopped,
    );

    document.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.code === "Space") {
        event.preventDefault();

        const now = Date.now();
        if (now - this._lastSpacebarPressTime < GameUI.SPACEBAR_DEBOUNCE_MS) {
          return;
        }
        this._lastSpacebarPressTime = now;

        if (this._spinButton.isVisible()) {
          if (!LoadingScreen.isLoaded) {
            console.log(
              `spin buttin is visible ${this._spinButton.isVisible()}`,
            );
            EventSystem.invoke(
              constants.UIEvents.CLICKED_UI_BUTTON,
              constants.UIButtonTypes.SPIN,
            );
          }
        } else {
          // When spin button isn't visible, spacebar acts as interactable area click.
          EventSystem.invoke(constants.UIEvents.CLICKED_INTERACTABLE_AREA);
        }
      }
    });
  }

  private _onCellPanelSpinStopped(): void {
  }

  private _initializeAssets(): void {
    const atlasUI = Assets.getAsset<TUIAssetAlias>("atlasUI");

    this._backgroundHorizontal = new PIXI.Sprite(
      atlasUI.textures["bottomBar.webp"],
    );
    this._backgroundHorizontal.anchor.set(0.5);

    this._backgroundVertical = new PIXI.Sprite(
      atlasUI.textures["bottomBarPortrait.webp"],
    );
    this._backgroundVertical.anchor.set(0.5);
    this._backgroundVertical.visible = false;
  }

  private _initializeComponents(): void {
    const atlasUI = Assets.getAsset<TUIAssetAlias>("atlasUI");

    this._bottomCoverArea = new PIXI.Graphics();
    this._bottomCoverArea.zIndex = 10;

    this._balanceInfoDisplay = new TitledInfo("Balance", "1,000.00");
    this._winningsInfoDisplay = new TitledInfo("Win", "0.00");

    const betButton = new Button(constants.UIButtonTypes.BET);
    betButton.activeGraphics = atlasUI.textures["betButton.webp"];
    betButton.onClick = this._onBetButtonClicked.bind(this);
    this._betButton = new TitledButton("Bet", "1.00", betButton);

    this._soundButton = new Button(constants.UIButtonTypes.SOUND);
    this._soundButton.activeGraphics = atlasUI.textures["soundOn.webp"];
    this._soundButton.inactiveGraphics = atlasUI.textures["soundOff.webp"];
    this._soundButton.onClick = this._onSoundButtonClicked.bind(this);

    this._cheatButton = new Button(constants.UIButtonTypes.CHEAT);
    this._cheatButton.activeGraphics = atlasUI.textures["buyButton.webp"];
    this._cheatButton.onClick = this._onCheatButtonClicked.bind(this);

    this._infoButton = new Button(constants.UIButtonTypes.INFO);
    this._infoButton.activeGraphics = atlasUI.textures["info.webp"];
    this._infoButton.onClick = this._onInfoButtonClicked.bind(this);

    this._autoplayButton = new Button(constants.UIButtonTypes.INFO);
    this._autoplayButton.activeGraphics =
      atlasUI.textures["autoplayButton.webp"];
    this._autoplayButton.onClick = this._onAutoplayButtonClicked.bind(this);

    this._autoplayValueText = new PIXI.Text({
      text: "",
      style: {
        fill: "0xfb6f2e",
        fontWeight: "bold",
        fontFamily: "uiFont",
        fontSize: 40,
      },
    });
    this._autoplayValueText.anchor.set(0.5);

    this._swiftButton = new Button(constants.UIButtonTypes.SWIFT);
    this._swiftButton.activeGraphics = atlasUI.textures["fastOn.webp"];
    this._swiftButton.inactiveGraphics = atlasUI.textures["fastOff.webp"];
    this._swiftButton.onClick = this._onSwiftButtonClicked.bind(this);
    this._swiftButton.setActive(false);

    this._spinButton = new Button(constants.UIButtonTypes.SPIN, false);
    this._spinButton.activeGraphics = atlasUI.textures["spinButton.webp"];
    this._spinButton.inactiveGraphics = atlasUI.textures["stopButton.webp"];
    this._spinButton.onClick = this._onSpinButtonClicked.bind(this);
    // this._buyButton;
    // this._menuButton;
    // this._homeButton;

    this._betsPopup = new BetsPopup();
    this._autoplayPopup = new AutoplayPopup();
    this._infoPopup = new InfoPopup();
  }

  private _parentAssets(): void {
    this._view.addChild(this._backgroundHorizontal);
    this._view.addChild(this._backgroundVertical);
  }

  private _parentComponents(): void {
    this._view.addChild(this._bottomCoverArea);

    this._view.addChild(this._balanceInfoDisplay.view);
    this._view.addChild(this._winningsInfoDisplay.view);

    this._view.addChild(this._betButton.view);
    this._view.addChild(this._soundButton.view);
    this._view.addChild(this._cheatButton.view);
    this._view.addChild(this._infoButton.view);
    this._view.addChild(this._autoplayButton.view);
    this._view.addChild(this._swiftButton.view);
    this._view.addChild(this._spinButton.view);
    this._spinButton.view.addChild(this._autoplayValueText);

    this._view.addChild(this._betsPopup.view);
    this._view.addChild(this._autoplayPopup.view);
    this._view.addChild(this._infoPopup.view);
  }

  private _onClickedInteractableArea(): void {
    if (this._autoplayPopup.isVisible && gameEngine.isAutoplayActive) {
      gameEngine.resetAutoplay();
      this._autoplayPopup.resetButtons();
    }

    this._betsPopup.setVisible(false);
    this._cheatsPopup.setVisible(false);
    this._autoplayPopup.setVisible(false);
    this._autoplayPopup.resetButtons();
    this._infoPopup.setVisible(false);

    if (this._readyToPlaySpinStopSound && !this._spinButton.isVisible()) {
      console.log("spin quick stop audio play");
      Audio.play("sndCslStop");
      this._readyToPlaySpinStopSound = false;
    }

    if (!gameEngine.gameActionData) {
      return;
    }

    if (gameEngine.gameActionData) {
      if (gameEngine.gameActionData.state === EGameState.BONUS_TO_MAIN) {
        this._balanceInfoDisplay.setInfoText(
          gameEngine.formatter.format(gameEngine.gameActionData.balance),
        );

        this._winningsInfoDisplay.setInfoText(
          gameEngine.formatter.format(gameEngine.gameActionData.totalWin),
        );
      }
    }
  }

  private _onClickedUIButton(buttonType: TGameUIButtonType, args: any): void {
    switch (buttonType) {
      case "spin":
        if (!gameEngine.isAutoplayActive) {
          this._spinButton.setVisible(false);
        }

        this._swiftButton.setVisible(false);
        this._cheatButton.setVisible(false);
        this._autoplayButton.setVisible(false);

        if (this._visiblePopup) {
          this._visiblePopup.setVisible(false);
        }

        if (gameEngine.gameActionData && gameEngine.gameActionData.state === EGameState.HOLD) {
          Audio.play("sndBonusStartButton");
        } else {
          Audio.play("sndCslSpin");
        }
        break;
      case "popupButton":
      case "sound":
      case "bet":
      case "cheat":
      case "info":
      case "autoplay":
      case "swift":
      case "action":
      case "betSelection":
        Audio.play("sndCslGeneric");
        break;
      case "betSelection":
        console.log(args);
        this._betButton.buttonText = args as string;
        break;
    }
  }

  private _onClickedRigButton(_: string): void {
    this._spinButton.setVisible(false);
    this._swiftButton.setVisible(false);
    this._cheatButton.setVisible(false);
    this._autoplayButton.setVisible(false);

  }

  private _onGameInitialized(_: TInitialResponse): void {
    // This popup receives data from the initial game data,
    // hence why it is initialized here.
    this._cheatsPopup = new CheatsPopup();
    this._view.addChild(this._cheatsPopup.view);

    this._balanceInfoDisplay.setInfoText(
      gameEngine.formatter.format(gameEngine.gameInitialData.balance),
    );
  }

  private _onPerformedGameAction(data: TSpinResponse): void {
    this._readyToPlaySpinStopSound = true;

    // Since the player balance updates immediately after requesting
    // a game action, we must first show the balance decrementing by the
    // bet amount.
    this._balanceInfoDisplay.setInfoText(
      gameEngine.formatter.format(
        gameEngine.gameActionData.balance - gameEngine.bet,
      ),
    );

    if (gameEngine.gameActionData.state === EGameState.MAIN) {
      this._winningsInfoDisplay.setInfoText(gameEngine.formatter.format(0));
    }
  }

  private _onWinningPopupClosed(): void {
    if (
      gameEngine.gameActionData.state !== EGameState.MAIN_TO_BONUS &&
      gameEngine.gameActionData.state !== EGameState.BONUS &&
      gameEngine.gameActionData.state !== EGameState.BONUS_TO_MAIN
    ) {
      this._balanceInfoDisplay.setInfoText(
        gameEngine.formatter.format(gameEngine.gameActionData.balance),
      );

      gsap.delayedCall(0.2, () => {
        this._spinButton.setVisible(true);
        this._swiftButton.setVisible(true);
        this._cheatButton.setVisible(true);
        this._autoplayButton.setVisible(true);
      });
    }

    if (gameEngine.gameActionData.state === EGameState.MAIN) {
      this._winningsInfoDisplay.setInfoText(
        gameEngine.formatter.format(gameEngine.gameActionData.roundWin),
      );
    } else if (
      gameEngine.gameActionData.state === EGameState.BONUS ||
      gameEngine.gameActionData.state === EGameState.MAIN_TO_BONUS
    ) {
      this._winningsInfoDisplay.setInfoText(
        gameEngine.formatter.format(gameEngine.gameActionData.totalWin),
      );
    }
  }

  private _onSpinStopped(): void {
    if (
      gameEngine.gameActionData.state !== EGameState.MAIN_TO_BONUS &&
      gameEngine.gameActionData.state !== EGameState.BONUS &&
      gameEngine.gameActionData.state !== EGameState.BONUS_TO_MAIN
    ) {
      if (gameEngine.gameActionData.wins.length === 0) {
        gsap.delayedCall(0.2, () => {
          this._spinButton.setVisible(true);
          this._swiftButton.setVisible(true);
          this._cheatButton.setVisible(true);
          this._autoplayButton.setVisible(true);
        });
      }
    }
    this._readyToPlaySpinStopSound = false
  }

  private _onFreespinIntroClosed(): void {
    this._spinButton.setVisible(true);
    this._swiftButton.setVisible(true);
    this._cheatButton.setVisible(true);
    this._autoplayButton.setVisible(true);
  }

  private _onFreespinOutroClosed(): void {
    this._spinButton.setVisible(true);
    this._swiftButton.setVisible(true);
    this._cheatButton.setVisible(true);
    this._autoplayButton.setVisible(true);
  }

  private _onCellPanelShown(): void {
    this._spinButton.setVisible(true);
    this._swiftButton.setVisible(true);
    this._cheatButton.setVisible(true);
    this._autoplayButton.setVisible(true);
  }

  private _onBetButtonClicked(): void {
    if (this._visiblePopup) {
      this._visiblePopup.setVisible(false);
    }
    this._visiblePopup = this._betsPopup;
    this._betsPopup.setVisible(true);
  }

  private _isMuted: boolean = false;

  private _onSoundButtonClicked(): void {
    this._isMuted = !this._isMuted;

    // 2. Apply to Howler globally
    Howler.mute(this._isMuted);
  }

  private _onCheatButtonClicked(): void {
    if (this._visiblePopup) {
      this._visiblePopup.setVisible(false);
    }
    this._visiblePopup = this._cheatsPopup;
    this._cheatsPopup.setVisible(true);
  }
  private _onInfoButtonClicked(): void {
    if (this._visiblePopup) {
      this._visiblePopup.setVisible(false);
    }
    this._visiblePopup = this._infoPopup;
    this._infoPopup.setVisible(true);
  }
  private _onAutoplayButtonClicked(): void {
    if (this._visiblePopup) {
      this._visiblePopup.setVisible(false);
    }
    this._visiblePopup = this._autoplayPopup;
    this._autoplayPopup.setVisible(true);
    this._autoplayPopup.resetButtons();
  }
  private _onSwiftButtonClicked(): void {
    EventSystem.invoke(
      constants.UIEvents.CLICKED_SWIFT_BUTTON,
      this._swiftButton.enabled,
    );
  }
  private _onSpinButtonClicked(): void {
  }

  private _onLandscapeLayoutRequest(): void {
    this._bottomCoverArea.clear();
    this._bottomCoverArea
      .rect(-2048, 540, 4096, 512)
      .fill({ color: 0x000000, alpha: 2 });

    this._backgroundHorizontal.position.set(0, 470);
    this._backgroundHorizontal.visible = true;
    this._backgroundVertical.visible = false;

    const ratio: number = window.innerWidth / window.innerHeight;
    const k: number = Math.max(1.75 / ratio, 1);

    this._infoButton.view.position.set(-810 / k, 450);
    this._soundButton.view.position.set(-730 / k, 450);
    this._balanceInfoDisplay.view.position.set(-670 / k, 515);
    this._winningsInfoDisplay.view.position.set(0, 515);
    this._betButton.view.position.set(340 / k, 515);
    this._swiftButton.view.position.set(510 / k, 500);
    this._autoplayButton.view.position.set(600 / k, 500);
    this._cheatButton.view.position.set(820 / k, 300);
    this._spinButton.view.position.set(820 / k, 450);

    this._soundButton.setScale(1);
    this._cheatButton.setScale(1);
    this._infoButton.setScale(0.32);
    this._autoplayButton.setScale(1);
    this._swiftButton.setScale(1);
    this._spinButton.setScale(1);
  }

  private _onPortraitLayoutRequest(): void {
    this._bottomCoverArea.clear();
    this._bottomCoverArea
      .rect(-2048, 950, 4096, 512)
      .fill({ color: 0x000000, alpha: 2 });

    this._backgroundVertical.position.set(0, 800);
    // this._backgroundVertical.scale.set(2.3, 1);
    this._backgroundVertical.width =
      (constants.Screen.HEIGHT_PORTRAIT * window.innerWidth) /
      window.innerHeight;
    this._backgroundHorizontal.visible = false;
    this._backgroundVertical.visible = true;

    this._balanceInfoDisplay.view.position.set(-320, 880);
    this._winningsInfoDisplay.view.position.set(0, 880);
    this._betButton.view.position.set(350, 880);
    this._soundButton.view.position.set(-400, 650);
    this._cheatButton.view.position.set(220, 650);
    this._infoButton.view.position.set(-310, 740);
    this._autoplayButton.view.position.set(-220, 650);
    this._swiftButton.view.position.set(400, 650);
    this._spinButton.view.position.set(0, 650);

    this._soundButton.setScale(2);
    this._cheatButton.setScale(1.3);
    this._infoButton.setScale(0.5);
    this._autoplayButton.setScale(2);
    this._swiftButton.setScale(2);
    this._spinButton.setScale(1.5);
  }
}
